import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, createElementVNode as _createElementVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "mb-4 text-sm text-red-600"
}
const _hoisted_2 = { class: "grid grid-cols-1 lg:grid-cols-5 gap-6" }
const _hoisted_3 = { class: "lg:col-span-3 space-y-4" }
const _hoisted_4 = {
  key: 0,
  class: "text-sm text-gray-600"
}
const _hoisted_5 = {
  key: 0,
  class: "lg:col-span-2 space-y-4"
}
const _hoisted_6 = { class: "bg-white rounded-2xl border border-gray-200 p-5" }

import { computed, inject, onMounted, onUnmounted, ref } from 'vue'
import { useStore } from 'vuex'

import NeueLoading from '@/components/neue/NeueLoading.vue'
import { useMarketing } from '@/composables/useMarketing'
import { useToast } from '@/composables/useToast'

import MarketingAgentSaw from './MarketingAgentSaw.vue'
import MarketingMilestones from './MarketingMilestones.vue'
import MarketingMonthHeader from './MarketingMonthHeader.vue'
import MarketingPlayCard from './MarketingPlayCard.vue'
import MarketingScoreboard from './MarketingScoreboard.vue'
import { headerState, sortItems } from './marketingSeries'
import MarketingSetup from './MarketingSetup.vue'


export default {
  __name: 'MarketingHome',
  setup(__props) {

const LOCKED_STARTERS = [
  { key: 'first_approval', label: 'First plan approved', earned_at: null },
  { key: 'first_order', label: 'First order from a play', earned_at: null },
  { key: 'first_1k_month', label: 'First $1k month', earned_at: null }
]

const {
  agent,
  plans,
  currentPlan,
  performance,
  signals,
  building,
  loading,
  error,
  errorOf,
  load,
  loadSignals,
  loadPerformance,
  createAgent,
  updateAgent,
  onAgentSaved,
  editItem,
  approveItem,
  skipItem,
  cancelSend,
  redraft,
  markDone,
  waitForPlan,
  stopWaiting
} = inject('marketing', null) || useMarketing()
const { showToast } = useToast()
const store = useStore()

const busy = ref(false)
const busyItems = ref({})
const loadError = ref('')
const buildTimedOut = ref(false)

const handle = computed(() => store.state.loggedInUser?.attributes?.handle || '')
const state = computed(() => headerState({ agent: agent.value, plan: currentPlan.value, building: building.value }))
const orderedItems = computed(() => sortItems(currentPlan.value?.items || []))
const previousSummary = computed(
  () => plans.value.find(p => p.attributes?.status === 'closed')?.attributes?.summary || null
)
const recipients = computed(() => signals.value?.reachable_count ?? null)

// no_ai_checks reads as a gap to fix, so it is shown as a warning instead of a green check.
const WARNING_SIGNALS = { no_ai_checks: 'AI assistants not checked yet' }
const withoutWarningSignals = all =>
  Object.fromEntries(Object.entries(all || {}).filter(([key]) => !(key in WARNING_SIGNALS)))
const sawSignals = computed(() =>
  withoutWarningSignals(currentPlan.value?.attributes?.signals || signals.value?.signals)
)
const previewSignals = computed(() => {
  const reachable =
    recipients.value === null
      ? {}
      : { reachable: { fired: true, evidence: `${recipients.value} customers who said yes to texts` } }
  return { ...reachable, ...withoutWarningSignals(signals.value?.signals) }
})
const warnings = computed(() => {
  const list = Object.entries(WARNING_SIGNALS)
    .filter(([key]) => (currentPlan.value?.attributes?.signals || signals.value?.signals)?.[key]?.fired)
    .map(([, text]) => ({ text }))
  if (signals.value && !signals.value.brand_voice_present)
    list.push({ text: 'No brand voice set.', link: '/ai/agents/settings' })
  return list
})

// A text sends at the next slot; a kit play is the merchant's own work, measured by its code.
const approvedToast = item => {
  const a = item.attributes || {}
  if (a.sms_eligible) return 'Approved. It sends at the next slot.'
  return a.discount_code
    ? 'Approved. Your code is live; orders using it count here.'
    : 'Approved. Mark it done when you have finished.'
}

const attempt = async (fn, success) => {
  try {
    await fn()
    if (success) showToast(success, { type: 'success' })
  } catch (e) {
    showToast(errorOf(e), { type: 'error' })
  }
}

const run = async (fn, success) => {
  busy.value = true
  await attempt(fn, success)
  busy.value = false
}

const runItem = async (item, fn, success) => {
  busyItems.value = { ...busyItems.value, [item.id]: true }
  await attempt(fn, success)
  busyItems.value = { ...busyItems.value, [item.id]: false }
}

// A newer poll or a stop supersedes older ones; only the latest poll may report a result.
let pollGeneration = 0
const stopPolling = () => {
  pollGeneration++
  stopWaiting()
}
const awaitFirstPlan = async () => {
  const generation = ++pollGeneration
  buildTimedOut.value = false
  const found = await waitForPlan()
  if (generation !== pollGeneration) return
  if (found) await loadPerformance('30d')
  else buildTimedOut.value = true
}
const waitIfBuilding = () => {
  if (state.value === 'building') awaitFirstPlan()
}

const onCreate = settings =>
  run(async () => {
    await createAgent(settings)
    waitIfBuilding()
  })

const onSave = settings => run(() => updateAgent(settings), 'Settings saved.')

// Settings can be saved here (header buttons) or from the shell drawer; either way the poll follows.
const stopListening = onAgentSaved(settings => {
  if (settings.is_active === false || settings.pace === 'paused') stopPolling()
  else waitIfBuilding()
})

onMounted(async () => {
  await Promise.all([load(), loadSignals().catch(() => {})])
  loadError.value = error.value
  if (!agent.value) return
  if (currentPlan.value) await loadPerformance('30d')
  else waitIfBuilding()
})
onUnmounted(() => {
  stopListening()
  stopPolling()
})

return (_ctx, _cache) => {
  return (_openBlock(), _createBlock(NeueLoading, { loading: _unref(loading) }, {
    default: _withCtx(() => [
      (loadError.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_1, _toDisplayString(loadError.value), 1))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(MarketingMonthHeader, {
            agent: _unref(agent),
            plan: _unref(currentPlan),
            "previous-summary": previousSummary.value,
            handle: handle.value,
            building: _unref(building),
            onResume: _cache[0] || (_cache[0] = $event => (onSave({ pace: 'full' }))),
            onTurnOn: _cache[1] || (_cache[1] = $event => (onSave({ is_active: true })))
          }, null, 8, ["agent", "plan", "previous-summary", "handle", "building"]),
          (!_unref(agent) && !loadError.value)
            ? (_openBlock(), _createBlock(MarketingSetup, {
                key: 0,
                busy: busy.value,
                onCreate: onCreate
              }, null, 8, ["busy"]))
            : (_unref(agent))
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  (state.value === 'building')
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(2, (n) => {
                          return _createElementVNode("div", {
                            key: n,
                            "data-skeleton": "",
                            class: "bg-white rounded-2xl border border-dashed border-gray-300 p-5 animate-pulse",
                            "aria-hidden": "true"
                          }, [...(_cache[2] || (_cache[2] = [
                            _createElementVNode("div", { class: "h-2.5 w-2/5 bg-gray-100 rounded" }, null, -1),
                            _createElementVNode("div", { class: "h-2.5 w-4/5 bg-gray-100 rounded mt-3" }, null, -1),
                            _createElementVNode("div", { class: "h-2.5 w-3/5 bg-gray-100 rounded mt-3" }, null, -1)
                          ]))])
                        }), 64)),
                        (buildTimedOut.value)
                          ? (_openBlock(), _createElementBlock("p", _hoisted_4, " Taking longer than usual. We will keep trying; check back in a few minutes. "))
                          : _createCommentVNode("", true)
                      ], 64))
                    : _createCommentVNode("", true),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(orderedItems.value, (item) => {
                    return (_openBlock(), _createBlock(MarketingPlayCard, {
                      key: item.id,
                      item: item,
                      busy: Boolean(busyItems.value[item.id]),
                      recipients: recipients.value,
                      onApprove: $event => (runItem(item, () => _unref(approveItem)(item.id), approvedToast(item))),
                      onEdit: copy => runItem(item, () => _unref(editItem)(item.id, copy), 'Saved.'),
                      onSkip: reason => runItem(item, () => _unref(skipItem)(item.id, reason), 'Skipped.'),
                      onCancel: $event => (runItem(item, () => _unref(cancelSend)(item.id), 'Send cancelled.')),
                      onRedraft: $event => (runItem(item, () => _unref(redraft)(item.id), 'Redrafted.')),
                      onDone: $event => (runItem(item, () => _unref(markDone)(item.id), 'Marked done.'))
                    }, null, 8, ["item", "busy", "recipients", "onApprove", "onEdit", "onSkip", "onCancel", "onRedraft", "onDone"]))
                  }), 128))
                ], 64))
              : _createCommentVNode("", true)
        ]),
        (_unref(agent) || _unref(signals))
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              (_unref(agent))
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createVNode(MarketingScoreboard, {
                      summary: _unref(currentPlan)?.attributes?.summary || {},
                      "orders-by-day": _unref(performance)?.orders_by_day || null,
                      milestones: _unref(performance)?.milestones || null
                    }, null, 8, ["summary", "orders-by-day", "milestones"]),
                    _createVNode(MarketingAgentSaw, {
                      signals: sawSignals.value,
                      warnings: warnings.value
                    }, null, 8, ["signals", "warnings"])
                  ], 64))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createVNode(MarketingAgentSaw, {
                      title: "What it will work from",
                      signals: previewSignals.value,
                      warnings: warnings.value
                    }, null, 8, ["signals", "warnings"]),
                    _createElementVNode("section", _hoisted_6, [
                      _cache[3] || (_cache[3] = _createElementVNode("h3", { class: "text-sm font-bold text-gray-900" }, "Milestones", -1)),
                      _createVNode(MarketingMilestones, {
                        class: "mt-2",
                        milestones: LOCKED_STARTERS
                      })
                    ])
                  ], 64))
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["loading"]))
}
}

}